import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  employeesGetAll,
  locationSalesGetSalesByEmployee,
  type SalesByEmployeeEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Combobox } from "~/components/ui/combobox";
import { toConvexTableId } from "~/lib/convex-table-id";
import { optionalSearchString } from "~/lib/parse-enum-value";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

export const Route = createFileRoute("/_authenticated/reports/labor-summary")({
  component: LaborSummaryPage,
  validateSearch: (search: Record<string, unknown>) => ({
    location: optionalSearchString(search.location),
    employeeId: optionalSearchString(search.employeeId),
  }),
});

type WeeklyEarningsData = SalesByEmployeeEntry;

function LaborSummaryPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const employeeId = searchParams.employeeId;

  const { data: employeesResult } = useQuery(
    convexQuery(employeesGetAll, {
      paginationOpts: { numItems: 500, cursor: null },
    })
  );
  const employees =
    employeesResult?.page.map((e) => ({
      id: e.id,
      firstName: e.firstName,
      lastName: e.lastName,
    })) ?? [];

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Labor
            Summary.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">Labor Summary</h2>
        <p className="body text-foreground">
          View employee labor statistics and earnings information
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Combobox
          className="bg-background h-9 w-48 min-w-0 rounded-md p-2"
          dropdownClassName="w-48"
          items={employees.map((e) => ({
            label: `${e.firstName} ${e.lastName}`,
            value: e.id,
            keywords: [e.firstName, e.lastName],
          }))}
          onValueChange={(v) => {
            void navigate({
              from: "/reports/labor-summary",
              search: (prev) => ({
                ...prev,
                employeeId: v ?? undefined,
              }),
            });
          }}
          placeholder="Select employee (optional)..."
          value={employeeId ?? null}
        />
      </div>

      <LaborSummaryContent employeeId={employeeId} locationId={locationId} />
    </div>
  );
}

const columnHelper = createColumnHelper<WeeklyEarningsData>();

const LaborSummaryContent = ({
  locationId,
  employeeId,
}: {
  locationId: string;
  employeeId?: string | null;
}) => {
  const [dateRange] = useSearchParamsDateRange();

  const { data, isPending: isLoading } = useQuery(
    convexQuery(locationSalesGetSalesByEmployee, {
      dateRange: {
        from: dateRange.from?.getTime() ?? 0,
        to: dateRange.to?.getTime() ?? 0,
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  // Filter by employee if one is selected
  const filteredData = useMemo(() => {
    if (!data) {
      return [];
    }
    if (!employeeId) {
      return data;
    }
    return data.filter((item) => item.employee.id === employeeId);
  }, [data, employeeId]);

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("employee", {
          header: "Employee",
          cell: ({ getValue }) => {
            const employee = getValue();
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.employee.id),
          meta: {
            getCellForExport: (e: WeeklyEarningsData["employee"]) =>
              `${e.firstName} ${e.lastName}`,
            faceting: {
              title: "Employee",
              getOption: (e: WeeklyEarningsData["employee"]) => ({
                value: e.id,
                label: `${e.firstName} ${e.lastName}`,
              }),
            },
          },
        }),
        columnHelper.accessor("hoursWorked", {
          header: "Hours Worked",
          cell: ({ getValue }) => getValue()?.toFixed(1) || "0.0",
          meta: {
            getCellForExport: (d: WeeklyEarningsData["hoursWorked"]) => d,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: WeeklyEarningsData["amountSold"]) => d,
          },
        }),
        columnHelper.display({
          id: "estimatedWages",
          header: "Est. Wages (@$15/hr)",
          cell: ({ row }) => {
            const hoursWorked = row.original.hoursWorked || 0;
            const estimatedWages = hoursWorked * 15;
            return estimatedWages.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (row: WeeklyEarningsData) => {
              const hoursWorked = row.hoursWorked || 0;
              return hoursWorked * 15;
            },
          },
        }),
        columnHelper.display({
          id: "salesPerHour",
          header: "Sales/Hour",
          cell: ({ row }) => {
            const hoursWorked = row.original.hoursWorked;
            const amountSold = row.original.amountSold;
            if (!hoursWorked || hoursWorked === 0) {
              return "$0.00";
            }
            const salesPerHour = amountSold / hoursWorked;
            return salesPerHour.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (row: WeeklyEarningsData) => {
              const hoursWorked = row.hoursWorked;
              const amountSold = row.amountSold;
              if (!hoursWorked || hoursWorked === 0) {
                return 0;
              }
              return amountSold / hoursWorked;
            },
          },
        }),
      ] as ColumnDef<WeeklyEarningsData>[],
    []
  );

  if (isLoading) {
    return <LoadingPanel message="Loading labor summary data..." />;
  }

  return (
    <SalesDataTable
      columns={columns}
      data={filteredData}
      id="labor-summary-table"
    />
  );
};
